// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { CircularProgressComponent } from './circular-progress.component';
import { AppIconsType } from '@fc-shared/ui/icon/icons';
import { isMediaFile } from '@fc-shared/ui/media-view-dialog/media-view.service';
import { FilesUploadsService } from '@fc-core/files-uploads/services/files-uploads.service';
import { FileUploadState } from '@fc-core/files-uploads/models/FileUploadState';
import {
  EventSourceService,
  FileUpdate,
} from '@fc-core/services/event-sources/eventsource.service';
import { FilesApiService } from '@fc-core/files-uploads/services/files-api.service';
import { FileProcessingStateComponent } from './file-processing-state.component';
import { SkeletonModule } from '@fc-shared/ui/skeleton/skeleton.module';
import { ImageViewComponent } from '@fc-shared/ui/image-loader/image-view.component';

/**
 * @deprecated Use `fc-attachment-preview`
 * (`@fc-shared/ui/file-preview/attachment-preview.component`) instead.
 *
 * TODO: migrate the 12 remaining call sites and delete this component. Both tiles
 * already share `useAttachmentPreview` for upload/processing/SSE state; what still
 * differs is chrome, and each gap needs closing on `fc-attachment-preview` first:
 *  - `files` / `currentIndex` are required there because it opens the media dialog
 *    itself — the search-result call sites here pass a different list than they
 *    render, so those inputs need an optional `previewClicked` escape hatch.
 *  - no `<ng-content>` slot for extra menu items (project-media projects one).
 *  - `file` is a two-way `model` here but a one-way input + `fileUpdated` output there.
 *  - delete flags are inverted (`readonly`/`showDelete` vs `canDelete`), and
 *    `fc-attachment-preview` adds a confirm dialog this component doesn't have.
 *  - fixed 140x120 tile vs this fluid card; no upload-failure message line, and
 *    the filename is rendered without its extension.
 */
@Component({
  selector: 'ngx-file-card',
  template: `
    <div
      [class]="statusClasses()"
      class="flex flex-col rounded-lg border transition-colors duration-300 ease-in-out min-w-160 group cursor-pointer"
      (mouseenter)="isHovered.set(true)"
      (mouseleave)="isHovered.set(false)"
    >
      <div
        class="flex flex-col items-center justify-center rounded-md overflow-hidden bg-white h-100 mt-8 mb-0 mx-8 relative"
        [class.cursor-pointer]="canPreview()"
        (click)="onPreviewClick(); $event.stopPropagation()"
      >
        @if (haveThumbnail()) {
          <fc-image-view
            [photo]="file()"
            [width]="280"
            [height]="200"
            [shape]="'rectangle'"
            [thumbnailSize]="'md'"
            class="transition-transform duration-300 ease-in-out group-hover:scale-105 will-change-transform"
            style="backface-visibility: hidden; -webkit-backface-visibility: hidden;"
          />

          @if (isVideo()) {
            @if (hoverAnimationSource(); as animationSource) {
              <img
                [src]="animationSource"
                alt=""
                class="absolute inset-0 h-full w-full object-cover"
              />
            }

            <div
              class="size-40 rounded-full bg-primary-100 flex items-center justify-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              <fc-icon
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                color="primary600"
                [size]="16"
                icon="icon-play"
              ></fc-icon>
            </div>
          }
        } @else {
          <div
            [class.bg-primary-100]="!isFailed()"
            [class.bg-error-100]="isFailed()"
            class="size-40 rounded-full flex items-center justify-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            @if (isProcessing()) {
              <fc-file-processing-state
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              />
            } @else {
              <fc-icon
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                [color]="isFailed() ? 'error600' : 'primary600'"
                [size]="20"
                [icon]="cardIcon()"
              ></fc-icon>
            }
            @if (!isProcessing() && showProgress()) {
              <fc-circular-progress class="m-2" [progress]="progressValue()" />
            }
          </div>
        }
      </div>

      <div class="flex flex-row items-center justify-between p-8 gap-8">
        <span
          [title]="displayName()"
          [class.text-error-600]="isFailed()"
          [class.group-hover:text-primary-600]="!isFailed()"
          class="text-xs font-medium truncate transition-colors duration-300 ease-in-out"
          >{{ displayName() }}</span
        >
        @if (!readonly()) {
          @if (showActionsMenu()) {
            <fc-icon
              class="cursor-pointer"
              [matMenuTriggerFor]="menu"
              icon="icon-more"
              (click)="$event.stopPropagation()"
            ></fc-icon>
          } @else {
            <fc-icon
              class="cursor-pointer"
              [color]="isFailed() ? 'error600' : 'secondary600'"
              icon="icon-delete"
              (click)="emitDelete()"
            ></fc-icon>
          }
        }
      </div>

      @if (isFailed()) {
        <div class="px-8 pb-8 overflow-hidden">
          <span class="text-xs text-error-600 truncate max-w-full inline-block">
            {{ currentUpload()?.errorMessage ?? 'Upload failed' }}
          </span>
        </div>
      }

      <mat-menu #menu="matMenu">
        @if (showDelete()) {
          <button (click)="emitDelete()" mat-menu-item>
            <fc-icon icon="icon-delete"></fc-icon>
            <span>{{ isUploading() ? 'Cancel upload' : 'Delete' }}</span>
          </button>
        }
        @if (canDownload()) {
          <button (click)="downloadFile()" mat-menu-item>
            <fc-icon icon="icon-download"></fc-icon>
            <span>Download</span>
          </button>
        }
        <ng-content></ng-content>
      </mat-menu>
    </div>
  `,
  imports: [
    IconComponent,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    CircularProgressComponent,
    FileProcessingStateComponent,
    SkeletonModule,
    ImageViewComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileCardComponent {
  file = model.required<AttachmentModel>();
  readonly = input<boolean>(false);
  showDelete = input<boolean>(true);
  fileDeleted = output<number>();
  previewClicked = output<void>();
  filesUploadsService = inject(FilesUploadsService);
  eventSource = inject(EventSourceService);
  filesApi = inject(FilesApiService);
  statusClasses = computed(() => {
    if (this.isFailed()) {
      return 'bg-error-50 border-error-500';
    }
    return 'bg-white border-secondary-200';
  });
  isProcessing = computed(() => {
    if (this.isUploading() || this.isFailed()) return false;
    return this.file()?.processed === false;
  });

  displayName = computed(() => {
    return this.file()?.name;
  });

  currentUpload = computed<FileUploadState | null>(() => {
    return (
      this.filesUploadsService
        .uploads()
        .find((upload) => upload.id === this.file()?.id) ?? null
    );
  });

  isUploading = computed(() => {
    return this.currentUpload()?.status === 'uploading';
  });

  isFailed = computed(() => {
    return this.currentUpload()?.status === 'failed';
  });

  isVideo = computed(() => {
    return this.file()?.mimeType?.startsWith('video/');
  });

  protected readonly isHovered = signal(false);

  hoverAnimationSource = computed(() => {
    if (!this.isHovered()) return null;
    return this.file()?.previewAnimation || null;
  });
  cardIcon = computed<AppIconsType>(() => {
    if (this.isFailed()) return 'icon-refresh';
    if (!isMediaFile(this.file()) && this.file().processed === true)
      return 'icon-download';
    return 'icon-file';
  });

  showProgress = computed(() => {
    if (this.isFailed()) return false;
    if (this.isUploading()) return true;
    const file = this.file();
    return file ? file.processed === false : false;
  });

  progressValue = computed(() => {
    if (this.isUploading()) {
      return this.currentUpload()?.progress ?? 0;
    }
    if (this.showProgress()) {
      return this.currentUpload()?.progress ?? 90;
    }
    return 100;
  });

  constructor() {
    this.eventSource.filesUpdate$
      .pipe(takeUntilDestroyed())
      .subscribe((fileUpdate) => {
        void this.handleFileUpdateFromEventSource(fileUpdate);
      });
  }

  haveThumbnail = computed(() => {
    return !!this.file()?.thumbnails?.md?.url;
  });

  canPreview = computed(() => {
    if (this.isUploading() || this.isFailed()) return false;
    const file = this.file();
    if (!file) return false;
    if (file.processed === false) return false;
    return !!file.original;
  });

  showActionsMenu = computed(() => {
    return !this.isUploading() && !this.isFailed();
  });

  canDownload = computed(() => {
    const file = this.file();
    if (!file) return false;
    if (file.processed === false) return false;
    return !!file.original;
  });

  downloadFile() {
    const file = this.file();
    const originalUrl = file?.original;
    if (originalUrl) {
      forceDownload(originalUrl, file.name ?? '');
    }
  }

  retryUpload(): void {
    if (this.isFailed()) {
      this.filesUploadsService.retryUpload(this.file()?.id);
    }
  }

  async handleFileUpdateFromEventSource(fileUpdate: FileUpdate): Promise<void> {
    const currentFile = this.file();
    if (fileUpdate.id !== currentFile?.id) return;
    const media = await this.filesApi.getFile(fileUpdate.id);
    this.file.set(media);
  }

  onPreviewClick() {
    if (this.isFailed()) {
      this.retryUpload();
      return;
    }
    if (this.canPreview()) {
      this.previewClicked.emit();
    }
  }

  emitDelete() {
    this.fileDeleted.emit(this.file()?.id);
  }
}

export async function forceDownload(url: string, filename: string) {
  const response = await fetch(url);
  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename || 'file';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}
